import clsx from 'clsx';
import React, { useMemo } from 'react';
import {
  ApiFilterOperation,
  AutocompleteSource,
  DomainObject,
  Filter,
  FilterComponentProps,
  Select,
  TableFilterCells,
  TableFilterState,
  useEventCallback,
} from '@eas/common-web';
import { Operation, useSelectOperations } from '../filter-api';
import { useStyles } from '../filter-styles';

type AutocompleteFilterValue<TYPE extends DomainObject> =
  | TYPE
  | TYPE[]
  | string
  | null;

function isDomainObject(value: unknown): value is DomainObject {
  return typeof value === 'object' && value !== null && 'id' in value;
}

function getSelectedId<TYPE extends DomainObject>(
  value: AutocompleteFilterValue<TYPE>
) {
  return isDomainObject(value) ? value.id : undefined;
}

function constructAutocompleteFilters<TYPE extends DomainObject>(
  field: string,
  value: AutocompleteFilterValue<TYPE>
): Filter[] {
  const values = Array.isArray(value) ? value : value ? [value] : [];

  return values.filter(isDomainObject).map((object) => ({
    field,
    operation: ApiFilterOperation.EQ,
    value: typeof object === 'object' ? object?.id : undefined,
  }));
}

export function buildAutocompleteFilterState<TYPE extends DomainObject>(
  state: TableFilterState,
  field: string,
  operation: ApiFilterOperation,
  value: AutocompleteFilterValue<TYPE>,
  multiple: boolean
): TableFilterState {
  const filters = constructAutocompleteFilters(field, value);

  if (multiple) {
    if (operation === ApiFilterOperation.NOT) {
      return {
        ...state,
        operation: ApiFilterOperation.NOT,
        object: value,
        filters: filters.length
          ? [
              {
                operation: ApiFilterOperation.OR,
                filters,
              },
            ]
          : undefined,
      };
    }

    return {
      ...state,
      operation: ApiFilterOperation.OR,
      filters,
      object: value,
    };
  }

  const selectedId = getSelectedId(value);

  if (operation === ApiFilterOperation.NOT) {
    return {
      ...state,
      operation: ApiFilterOperation.NOT,
      value: selectedId,
      object: value,
      filters: filters.length ? filters : undefined,
    };
  }

  const rest = { ...state };
  delete rest.filters;

  return {
    ...rest,
    operation: ApiFilterOperation.EQ,
    value: selectedId,
    object: value,
  };
}

export function FilterAutocompleteCell<TYPE extends DomainObject>(
  dataHook: () => AutocompleteSource<TYPE>,
  labelMapper?: (option: TYPE) => string,
  multiple = false
) {
  const classes = useStyles();

  return useMemo(
    () =>
      function Cell(props: FilterComponentProps) {
        const { disabled, filter, onChangeState, state } = props;
        const operations = useSelectOperations();
        const operation =
          state.operation === ApiFilterOperation.NOT
            ? ApiFilterOperation.NOT
            : ApiFilterOperation.EQ;

        const FilterAutocomplete =
          TableFilterCells.useFilterAutocompleteCellFactory(
            dataHook,
            labelMapper
          );

        const MultipleFilterAutocomplete =
          TableFilterCells.useFilterMultipleAutocompleteCellFactory(
            dataHook,
            labelMapper
          );

        const handleChangeOperation = useEventCallback(
          (value: Operation | Operation[] | string | string[] | null) => {
            if (!value) {
              return;
            }

            onChangeState(
              buildAutocompleteFilterState(
                state,
                filter.filterkey,
                value as ApiFilterOperation,
                state.object ?? null,
                multiple
              )
            );
          }
        );

        const handleChangeAutocomplete = useEventCallback(
          (nextState: TableFilterState) => {
            onChangeState(
              buildAutocompleteFilterState(
                state,
                filter.filterkey,
                operation,
                nextState.object ?? null,
                multiple
              )
            );
          }
        );

        return (
          <div
            className={clsx(classes.root, {
              [classes.disabled]: disabled,
            })}
          >
            <div
              className={clsx(
                classes.selectWrapper,
                classes.compactSelectWrapper
              )}
            >
              <Select
                source={operations}
                value={operation}
                onChange={handleChangeOperation}
                valueIsId={true}
                clearable={false}
                disabled={disabled}
              />
            </div>
            <div className={classes.compactTextWrapper}>
              {!multiple && (
                <FilterAutocomplete
                  {...props}
                  onChangeState={handleChangeAutocomplete}
                />
              )}
              {multiple && (
                <MultipleFilterAutocomplete
                  {...props}
                  onChangeState={handleChangeAutocomplete}
                />
              )}
            </div>
          </div>
        );
      },
    [dataHook, multiple]
  );
}
